<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, {
    type ITableColumn,
    type ITableSortSettings,
    type TCheckboxOption,
    type TTableRow,
} from '@pocketprep/ui-kit'
import type { Teach } from '@pocketprep/types'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import MultiCheckboxFilter, { type TDropdownCheckboxMenu } from '@/components/Assignments/MultiCheckboxFilter.vue'
import AddStudentsSidePanel from '@/components/SidePanels/AddStudents.vue'
import { formatDate } from '@/utils'
import { studentModule } from '@/store/student/module'
import { groupModule } from '@/store/group/module'
import { licenseModule } from '@/store/license/module'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import { tableModule } from '@/store/table/module'
import { organizationModule } from '@/store/organization/module'
import { analyticsModule } from '@/store/analytics/module'

const {
    SidePanel,
    Search,
    Table,
    Checkbox,
    OverflowTooltip,
    Button: PocketButton,
    TablePagination,
    Tag,
    EmptyState,
} = UIKit

interface IMappedStudent extends TTableRow {
    id: string
    studentNameOrEmail: string
    dateAdded: string
    status: 'active' | 'pending' | 'archived'
    customGroups: string[]
    isAssigned: boolean
    studentInstructors: string[]
}

type TActiveFilters = {
    instructor: string[]
    customGroup: string[]
    status: string[]
}

const props = defineProps<{
    orgExamId: string
    examGuid: string
    assignedStudentsIds?: string[]
    disabledCheckboxStudents?: string[]
    autoFilterByInstructor?: boolean
}>()

const emit = defineEmits<{
    'close': []
    'studentsUpdated': [ string[] ]
}>()

const emitClose = () => {
    emit('close')
}

const brandColors = BrandColors
const isLoading = ref(false)
const selectedStudentIds = props.assignedStudentsIds ? ref([ ...props.assignedStudentsIds ]) : ref([])
const sortSettings = ref<ITableSortSettings>({ column: {
    name: 'Student',
    propName: 'studentNameOrEmail',
},
direction: 1 })
const showAddStudentSidePanel = ref(false)
const searchString = ref<string>('')
const activeFilters = ref<TActiveFilters>({ instructor: [], customGroup: [], status: [] })
const perPage = 10
const currentPage = ref(0)
const filterNameMap = { instructor: 'Instructor', customGroup: 'Group', status: 'Status' }
const studentColumns: ITableColumn[] = [{
    name: 'Student',
    propName: 'studentNameOrEmail',
    styles: {
        width: '225px',
    },
    labelStyles: {
        paddingLeft: '29px',
    },
}, {
    name: 'Date Added',
    propName: 'dateAdded',
}, {
    name: 'Status',
    propName: 'status',
}]

const orgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrg = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const studentsInCurrentExam = computed(() => {
    return studentModule.getters.getStudentsForOrgExamId(props.orgExamId)
})

const orgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const studentLicenseLib = computed(() => {
    return licenseModule.getters.getStudentLicenseLib()
})

const orgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const orgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const mappedCheckboxValue = computed(() => {
    return studentsSelected.value === 'all' ? true : studentsSelected.value === 'none' ? false : null
})

const assignedStudents = computed(() => {
    if (!props.assignedStudentsIds?.length) {
        return []
    }
    return props.assignedStudentsIds.map(studentId => orgStudents.value[studentId])
})

const customOrgGroupsInCurrentExam = computed(() => {
    return Object.values(orgGroups.value).filter(g => {
        const orgInstructor = g.owner
            && orgInstructors.value?.[g.owner.objectId]
        return (g.type !== 'private'
            || currentOrgInstructor.value?.objectId === g.owner?.objectId
            || currentOrgInstructor.value?.isAdmin)
        && (currentOrgInstructor.value?.isAdmin || yourOrgExamIds.value.includes(g.orgExam.objectId))
        && orgInstructor?.status !== 'pending'
        && g.type !== 'system'
        && g.orgExam.objectId === props.orgExamId
    })
})

const currentExamSystemGroups = computed(() => {
    return groupModule.getters.searchCurrentOrgGroups({
        examId: props.orgExamId,
        type: 'system',
    })
})

const orgInstructorIdsByStudentIdLib = computed(() => {
    const instructorIdsByStudentId = currentExamSystemGroups.value.reduce((acc, group) => {
        const instructor = group.owner?.objectId
        const studentsInGroup = group.orgStudents
        if (!instructor) {
            return acc
        }
        const groupStudentIds = studentsInGroup.map(student => student.objectId)
        groupStudentIds.forEach(studentId => {
            if (!acc[studentId]) {
                acc[studentId] = []
            }
            acc[studentId].push(instructor)
        })
        return acc
    }, {} as { [studentId: string]: string[] })

    return instructorIdsByStudentId
})

const allInstructorIdsForCurrentExam = computed(() => {
    const instructorIds = currentExamSystemGroups.value
        .map(g => g.owner?.objectId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    return [ ...new Set(instructorIds) ]
})

const customGroupsInCurrentExamByStudentIdLib = computed(() => {
    return Object.values(orgStudents.value).reduce((acc, student) => {
        acc[student.objectId] = customOrgGroupsInCurrentExam.value
            .filter(gr => gr.orgStudents.find(st => st.objectId === student.objectId))
        return acc
    }, {} as { [studentId: string]: Teach.Class.OrgGroupJSON[] })
})

const hasSelectionChanged = computed(() => {
    const oldSelection = props.assignedStudentsIds
    const newSelection = selectedStudentIds.value
    if (oldSelection?.length !== newSelection.length) {
        return true
    }
    const oldSelectionSet = new Set(oldSelection)
    for (const item of newSelection) {
        if (!oldSelectionSet.has(item)) {
            return true
        }
    }

    return false
})

const previouslySelectedCustomGroups = computed(() => {
    return tableModule.getters.getTableState('assignments')?.filters?.customGroup
})

const studentFilters = computed(() => {
    const availableFilters: TDropdownCheckboxMenu[] = []
    if (!mappedStudents.value.length) {
        return []
    }
    availableFilters.push({
        filterType: 'instructor',
        filterMenuLabel: 'filter by instructor',
        items: allInstructorIdsForCurrentExam.value.map(instructorId => {
            return {
                label: getInstructorName(instructorId),
                id: instructorId,
                enabled: activeFilters.value.instructor.includes(instructorId),
            }
        }),
        expanded: true,
        allowCollapse: true,
    })
    if (customOrgGroupsInCurrentExam.value.length) {
        availableFilters.push({
            filterType: 'customGroup',
            filterMenuLabel: 'filter by custom group',
            items: customOrgGroupsInCurrentExam.value.map(customGroup => ({
                label: customGroup.name,
                id: customGroup.objectId,
                enabled: previouslySelectedCustomGroups.value?.includes(customGroup.name) || false,
            })),
            expanded: true,
            allowCollapse: true,
        })
    }
    availableFilters.push({
        filterType: 'status',
        filterMenuLabel: 'filter by status',
        items: [{ label: 'Active', id: 'active', enabled: false }, { label: 'Pending', id: 'pending', enabled: false }],
        expanded: true,
        allowCollapse: true,
    })

    return availableFilters
})

const mappedStudents = computed(() => {
    const currentOrgExamId = props.orgExamId
    const currentOrgExamGuid = examModule.getters.getExamGuidForOrgExamId(currentOrgExamId)
    const allStudentsInExam = [ ...studentsInCurrentExam.value, ...assignedStudents.value ]
        .filter((student, index, self) =>
            self.findIndex(i => i?.objectId === student?.objectId) === index
        )
    return allStudentsInExam.reduce((acc, studentPointer) => {
        const student = orgStudents.value[studentPointer?.objectId || '']
        const studentUser = student?.user && orgStudentUsers.value[student?.user.objectId]
        const studentExamLicense = studentLicenseLib.value[studentPointer?.objectId || '']?.find(license =>
            license.examGuid === currentOrgExamGuid)

        if ((!studentUser && !student) || !studentExamLicense) {
            return acc
        }

        const studentNameOrEmail = [ student.displayFirstName, student.displayLastName ].join(' ').trim()
            || [ studentUser?.firstName, studentUser?.lastName ].join(' ').trim()
            || studentExamLicense?.userEmail || ''
        const dateAdded = studentUser ? studentUser.createdAt : 'N/A'
        const status = getStudentStatusBasedOnLicense(studentExamLicense.status)
        const customGroups = customGroupsInCurrentExamByStudentIdLib.value?.[student.objectId]?.map(gr => gr.objectId)
            || []
        const isAssigned = selectedStudentIds.value.includes(student.objectId)
        const studentInstructors = orgInstructorIdsByStudentIdLib.value[studentPointer?.objectId || ''] || []

        acc.push({
            id: student.objectId,
            studentNameOrEmail,
            dateAdded,
            status,
            customGroups,
            isAssigned,
            studentInstructors,
        })

        return acc
    }, [] as IMappedStudent[]).sort((a, b) => {
        if (sortSettings.value.column && a && b) {
            if (sortSettings.value.column.name === 'Student') {
                return sortSettings.value.direction * a.studentNameOrEmail
                    .localeCompare(b.studentNameOrEmail)
            }
            if (sortSettings.value.column.name === 'Date Added') {
                if (!a.dateAdded) {
                    return sortSettings.value.direction * 1
                }
                if (!b.dateAdded) {
                    return sortSettings.value.direction * -1
                }
                const dateA = new Date(a.dateAdded)
                const dateB = new Date(b.dateAdded)
                return sortSettings.value.direction * (dateA.getTime()) - (dateB.getTime())
            }
            if (sortSettings.value.column.name === 'Status') {
                return sortSettings.value.direction * (a.status).localeCompare(b.status)
            }
        }

        return -1
    })
})

const filteredStudents = computed(() => {
    const currentActiveFilters = activeFilters.value
    return mappedStudents.value.filter(student => {
        return student.studentNameOrEmail.toLowerCase().includes(searchString.value.toLowerCase())
            && (!currentActiveFilters.status.length || currentActiveFilters.status.includes(student.status))
            && (!currentActiveFilters.customGroup.length || student.customGroups.length && student.customGroups
                .some(customGroup => currentActiveFilters.customGroup.includes(customGroup))
            )
            && (!currentActiveFilters.instructor.length || student.studentInstructors.length
            && student.studentInstructors.some(instructor => currentActiveFilters.instructor.includes(instructor))
            )
    })
})

const pagedFilteredStudents = computed(() => {
    return filteredStudents.value.slice(currentPage.value * perPage, (currentPage.value * perPage) + perPage)
})

const studentsSelected = computed(() => {
    return (selectedStudentIds.value.length && filteredStudents.value.length
        && selectedStudentIds.value.length === filteredStudents.value.length)
        ? 'all'
        : selectedStudentIds.value.length === 0 && filteredStudents.value ? 'none' : 'intermediate'
})

const getStudentStatusBasedOnLicense = (licenseStatus: 'available' | 'pending' | 'consumed' | 'archived') => {
    if (licenseStatus === 'available' || licenseStatus === 'pending') {
        return 'pending'
    } else if (licenseStatus === 'consumed') {
        return 'active'
    } else {
        return 'archived'
    }
}

const getCheckboxStyles = (rowId: string) => {
    if (props.disabledCheckboxStudents?.includes(rowId)) {
        return {
            background: `${brandColors.brandBlue}66`,
            border: 'none',
        }
    }
}

const getInstructorName = (instructorId: string) => {
    const instructorUserId = orgInstructors.value?.[instructorId]?.user.objectId
    const instructorUser = instructorUserId && currentOrgInstructorUsers.value?.[instructorUserId]
    if (!instructorUser) {
        return 'unknown'
    }
    const firstName = instructorUser.firstName || ''
    const lastName = instructorUser.lastName || ''
    if (!firstName && !lastName) {
        return instructorUser.username
    }
    return `${firstName} ${lastName}`
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const selectAllStudents = () => {
    const studentIdsWithAssignmentCompleted = props.disabledCheckboxStudents || []
    if (studentsSelected.value === 'all'
        || (studentsSelected.value === 'intermediate' && !props.disabledCheckboxStudents?.length)) {
        selectedStudentIds.value = [ ...studentIdsWithAssignmentCompleted ]
    } else {
        const newSelectionSet = new Set([ ...studentIdsWithAssignmentCompleted,
            ...filteredStudents.value.map(student => student.id) ])
        selectedStudentIds.value = [ ...newSelectionSet ]
    }
}

const updateFilter = (updatedFilter: { filterType: string; item: TCheckboxOption }) => {
    const filterType = updatedFilter.filterType
    const item = updatedFilter.item
    if (filterType in activeFilters.value) {
        const filter = filterType as keyof TActiveFilters
        if (item.enabled) {
            activeFilters.value[filter] = [ ...activeFilters.value[filter], item.id ]
        } else {
            activeFilters.value[filter] = activeFilters.value[filter].filter(itemId => itemId !== item.id)
            if (filterType === 'customGroup') {
                const existingAssignmentsTableState = tableModule.getters.getTableState('assignments')
                if (existingAssignmentsTableState) {
                    existingAssignmentsTableState.filters.customGroup = existingAssignmentsTableState?.filters
                        .customGroup?.filter(g => g !== item.label) || []
                    tableModule.actions.saveTableState(existingAssignmentsTableState)
                }
            }
        }
    }

    if (filteredStudents.value.length <= perPage) {
        currentPage.value = 0
    }
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const toggleStudent = (studentId: string, isSelected: boolean) => {
    const selectedStudents = selectedStudentIds.value
    const toggledStudentIndex = selectedStudents.indexOf(studentId)

    if (isSelected && toggledStudentIndex === -1) {
        selectedStudents.push(studentId)
    } else if (!isSelected && toggledStudentIndex !== -1) {
        selectedStudents.splice(toggledStudentIndex, 1)
    }

    selectedStudentIds.value = selectedStudents
}

const triggerTableFilteredEvent = () => {
    if (currentOrg.value) {
        analyticsModule.actions.amplitudeTrack('Table Filtered', {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            tableName: 'Assignment Students',
            didFilterInstructor: activeFilters.value.instructor.length ? true : false,
            instructorFiltersCount: activeFilters.value.instructor.length,
            didFilterCustomGroup: activeFilters.value.customGroup.length ? true : false,
            customGroupFiltersCount: activeFilters.value.customGroup.length,
            didFilterStatus: activeFilters.value.status.length ? true : false,
        })
    }
}

const emitAssignedStudents = () => {
    emit('studentsUpdated', selectedStudentIds.value)
}

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        organizationModule.actions.fetchCurrentOrganization(),
        examModule.actions.fetchCurrentOrgExams(),
        studentModule.actions.fetchCurrentOrgStudents(),
        groupModule.actions.fetchCurrentOrgGroups(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    if (currentOrgInstructor.value && !currentOrgInstructor.value?.isAdmin && props.autoFilterByInstructor) {
        activeFilters.value.instructor.push(currentOrgInstructor.value.objectId)
    }

    isLoading.value = false
})
</script>

<template>
    <Portal to="sidepanel">
        <AddStudentsSidePanel
            v-if="showAddStudentSidePanel"
            @close="() => showAddStudentSidePanel = false"
        />
        <div class="assign-students" v-if="!isLoading">
            <SidePanel
                :isTeachSidePanel="true"
                @close="emitClose"
                name="Assign Students to Assignment"
            >
                <div class="assign-students__help-text">
                    Students added to this assignment will be able to take it on their Pocket Prep app.
                </div>
                <div class="assign-students__filter-container">
                    <Search
                        v-model="searchString"
                        class="assign-students__search"
                        placeholder="Search"
                        type="white"
                    />
                    <MultiCheckboxFilter
                        :dropdownMenus="studentFilters"
                        :filterNameMap="filterNameMap"
                        filterLabel="Filter Students"
                        @updateFilters="updateFilter"
                        @dropdownClosed="triggerTableFilteredEvent"
                    />
                </div>
                <div class="assign-students__table-actions">
                    <div class="assign-students__table-actions-select-container">
                        <Checkbox
                            :modelValue="mappedCheckboxValue"
                            class="assign-students__table-actions-checkbox"
                            @click="selectAllStudents"
                        />
                        <div class="assign-students__table-actions-num-selected">
                            {{ selectedStudentIds.length }} of {{ filteredStudents.length }} selected
                        </div>
                    </div>
                    <TablePagination
                        v-if="pagedFilteredStudents.length"
                        :per-page="perPage"
                        unit="Students"
                        :current-page="currentPage"
                        :total="filteredStudents.length"
                        @changePage="changePage"
                    />
                </div>
                <Table
                    :columns="studentColumns"
                    :rows="pagedFilteredStudents"
                    :sortSettings="sortSettings"
                    :sortToggleOrder="[ 1, -1 ]"
                    :showHeader="false"
                    @sort="updateSortSettings"
                >
                    <template #tableCellValue="{ row, column }">
                        <template v-if="column.propName === 'studentNameOrEmail'">
                            <div class="assign-students__checkbox-name">
                                <Checkbox
                                    :modelValue="selectedStudentIds.includes(row.id)"
                                    :disabled="props.disabledCheckboxStudents?.includes(row.id)"
                                    :checkboxStyles="getCheckboxStyles(row.id)"
                                    class="assign-students__checkbox"
                                    @update:modelValue="toggleStudent(row.id, $event)"
                                />
                                <div
                                    class="assign-students__name"
                                    :class="{'assign-students__name--pending-or-archived':
                                        row.status === 'pending' || row.status === 'archived'}"
                                >
                                    <OverflowTooltip>
                                        {{ row.studentNameOrEmail }}
                                    </OverflowTooltip>
                                </div>
                            </div>
                        </template>
                        <template v-if="column.propName === 'dateAdded'">
                            {{ row.dateAdded !== 'N/A' ? formatDate(row.dateAdded) : row.dateAdded }}
                        </template>
                        <template v-if="column.propName === 'status'">
                            <div class="students__status">
                                <Tag
                                    v-if="row.status === 'pending'"
                                    :isPendingTag="true"
                                    size="small"
                                    :tagBorder="`1px solid ${brandColors.bananaBread}`"
                                >
                                    <template #tagMessage>
                                        Pending
                                    </template>
                                </Tag>
                                <Tag
                                    v-else-if="row.status === 'archived'"
                                    :isArchivedTag="true"
                                    size="small"
                                >
                                    <template #tagMessage>
                                        Archived
                                    </template>
                                </Tag>
                                <Tag
                                    v-else
                                    :isActiveTag="true"
                                    size="small"
                                    :tagBorder="`1px solid ${brandColors.green}`"
                                >
                                    <template #tagMessage>
                                        Active
                                    </template>
                                </Tag>
                            </div>
                        </template>
                    </template>
                </Table>
                <div
                    class="assign-students__empty-state-container"
                    v-if="!studentsInCurrentExam.length"
                >
                    <EmptyState
                        class="assign-students__empty-state"
                        :title="'There\'s nobody here yet'"
                        message="Get started by adding some students to this exam."
                    >
                        <template #imageRow>
                            <div />
                        </template>
                    </EmptyState>
                    <PocketButton
                        class="assign-students__add-students-btn"
                        @click="showAddStudentSidePanel = true"
                        @mousedown.prevent
                    >
                        Add Students
                    </PocketButton>
                </div>
                <EmptyState
                    v-else-if="!pagedFilteredStudents.length"
                    class="assign-students__empty-state"
                    title="No Results"
                    message="There are no students that match your filter and search criteria."
                >
                    <template #imageRow>
                        <div />
                    </template>
                </EmptyState>
                <template #action>
                    <PocketButton
                        type="primary"
                        :disabled="!hasSelectionChanged"
                        @click="emitAssignedStudents"
                    >
                        {{ props.assignedStudentsIds?.length
                            ? 'Update Assigned Students' : 'Add Students to Assignment' }}
                    </PocketButton>
                </template>
            </SidePanel>
        </div>
    </Portal>
</template>

<style lang="scss" scoped>

.assign-students {

    &__help-text {
        font-size: 15px;
        line-height: 22px;
        margin-bottom: 24px;
    }

    &__checkbox-name {
        display: flex;
        align-items: center;
    }

    &__checkbox {
        margin-right: 10px;
    }

    &__empty-state-container {
        text-align: center;
    }

    &__empty-state {
        margin: auto;
        margin-top: 40px;
    }

    &__add-students-btn {
        margin: auto;
        margin-top: 22px;
    }

    &__name {
        width: calc(100% - 28px);

        &--pending-or-archived {
            color: $ash;
        }
    }

    &__search {
        width: 266px;
        margin-bottom: 6px;
    }

    &__table-actions {
        display: flex;
        padding: 0 12px;
        margin-bottom: 11px;
        justify-content: space-between;
    }

    &__table-actions-num-selected {
        font-size: 12px;
        line-height: 18px;
    }

    &__table-actions-select-container {
        display: flex;
    }

    &__table-actions-checkbox {
        margin-right: 11px;
    }

    &__filter-container {
        position: relative;
        margin-bottom: 12px;

        :deep(.multi-filter) {
            flex-direction: column;
        }

        :deep(.multi-filter__dropdown-btn) {
            position: absolute;
            left: 274px;
            top: -38px;
        }

        :deep(.multi-filter__active-filters-container) {
            margin-left: 8px;
        }
    }

    :deep(.uikit-table__column-labels) {
        border-top: 1px solid rgba($pewter, 0.3);
        background-color: $gray-background;
    }
}
</style>
